/**
 * 文件传输 v1 会话(控制端):`file:hello` 握手 + 发送状态机 + 接收状态机。
 *
 * 镜像 `negotiation.ts` 的模块级会话态(与 WebRTCConnector 单例语义一致),peer / data
 * channel 重建时 `resetFileTransferState` 复位;wire 编解码全走 `protocol.ts`。
 * 控制端既可发送(§8,offer→chunk→complete)也可接收(§9,host→controller)。§4:同时
 * 只允许一个 active transfer(收或发)。付费 gate 在 host 侧(§5):Console guest-only 不判 entitlement,
 * 只消费 host `file:hello.capabilities` 决定发送入口可用性。
 */
import { currentFileChannel } from "../webrtc";
import { waitForDataChannelDrain } from "./data-channel-drain";
import { fileHashWorker } from "./hash-worker-client";
import {
	BACKPRESSURE_HIGH_BYTES,
	BACKPRESSURE_LOW_BYTES,
	decodeChunkFrame,
	encodeChunkFrame,
	encodeFileControl,
	type FileAccept,
	type FileCapability,
	type FileComplete,
	type FileError,
	type FileErrorCode,
	type FileHello,
	type FileOffer,
	type FileReject,
	HELLO_ID,
	HELLO_TIMEOUT_MS,
	isValidAcceptChunkSize,
	maxMessageSizeSupportsV1,
	negotiateChunkSize,
	newTransferId,
	parseFileControl,
	resolveGlare,
	sanitizeFileName,
} from "./protocol";

/**
 * 本端(控制端)声明的能力(§7.1)。M5 接收确认 UI 落地后,接收端可见 / 可拒绝 / 可取消
 * (§1 / §5 / §7.2),故恒声明 send + receive,对端可据此开放"发送到控制端"入口。
 */
const LOCAL_CAPABILITIES: FileCapability[] = ["send", "receive"];
/**
 * 本端单文件大小上限,同时用于发送上限、接收拒绝阈值与 hello 广播的 maxFileSize。
 * Console 已把增量 Hash 与 Blob(chunks)移入 Worker，避免主线程连续大缓冲；128 MiB
 * 仍需通过最低配置浏览器的峰值内存、long task 与 WebRTC 掉帧门禁后才能对外开放。
 */
const LOCAL_MAX_FILE_SIZE = 128 * 1024 * 1024;
/** 本端愿意接收的单条 data channel message 上限(§7.1)。frame ≤ 68 KiB,留足余量。 */
const LOCAL_MAX_MESSAGE_SIZE = 256 * 1024;
/** 本端 chunkSize 偏好(发送协商 / 接收 accept,§6.2 / §7.3)。 */
const LOCAL_PREFERRED_CHUNK_SIZE = 64 * 1024;

// ── 对端 hello 协商态 ────────────────────────────────────────────────

let peerHelloReceived = false;
let peerCapabilities: ReadonlySet<string> = new Set();
let peerMaxMessageSize = 0;
let peerMaxFileSize = 0;
let helloTimer: ReturnType<typeof setTimeout> | undefined;

// ── 发送态 / 接收态(§4 单 active)────────────────────────────────────

export type FileSendCallbacks = {
	onStatus?: (message: string) => void;
	onProgress?: (p: { sentBytes: number; totalBytes: number }) => void;
	onError?: (message: string) => void;
	onComplete?: () => void;
};

type ActiveSend = {
	id: string;
	file: File;
	size: number;
	chunkSize: number;
	offset: number;
	seq: number;
	cb: FileSendCallbacks;
	done: boolean;
};

type ActiveReceive = {
	id: string;
	name: string;
	size: number;
	sha256: string;
	mime: string;
	workerTaskId: string;
	received: number;
	nextSeq: number;
	finalizing: boolean;
};

type ActivePreparation = {
	taskId: string;
	cb: FileSendCallbacks;
};

/** 已收到、等待用户确认的 incoming offer(§5 / §7.2 接收确认)。 */
export type IncomingOffer = {
	id: string;
	name: string;
	size: number;
	mime: string;
};

/** UI 注册的接收侧回调(§5 接收确认 / 进度 / 状态 / host 能力)。 */
export type FileTransferUICallbacks = {
	/** 有新 offer 待确认时传入 offer;清空(确认/拒绝/取消/复位)时传入 null。 */
	onIncomingOffer?: (offer: IncomingOffer | null) => void;
	onReceiveProgress?: (p: {
		receivedBytes: number;
		totalBytes: number;
	}) => void;
	onReceiveStatus?: (message: string) => void;
	/**
	 * 对端(host)文件传输能力变化(§5:控制端只消费 host 能力决定 UI)。host 付费 gate 未通过时
	 * 不声明能力,这里两者皆 false → UI 隐藏/禁用发送入口。hello 到达 / 复位时回调。
	 */
	onHostCapabilities?: (caps: {
		canReceive: boolean;
		canSend: boolean;
	}) => void;
};

let activeSend: ActiveSend | null = null;
let activeReceive: ActiveReceive | null = null;
let activePreparation: ActivePreparation | null = null;
/** 暂存的待确认 offer(sha256 留在此处,accept 时移交 activeReceive)。 */
let incomingOffer: (IncomingOffer & { sha256: string }) | null = null;
let uiCallbacks: FileTransferUICallbacks = {};

/**
 * 当前对端(host)的文件传输能力(§5:控制端据此决定 UI)。canReceive 与 `isPeerReadyToReceive`
 * 同口径(hello + receive 能力 + maxMessageSize 达标);canSend = host 声明可主动发送。
 */
const hostCapabilities = (): { canReceive: boolean; canSend: boolean } => ({
	canReceive:
		peerHelloReceived &&
		peerCapabilities.has("receive") &&
		maxMessageSizeSupportsV1(peerMaxMessageSize),
	canSend: peerHelloReceived && peerCapabilities.has("send"),
});

/**
 * 注册接收侧 UI 回调。组件挂载时调用;返回注销函数(卸载时调用,避免回调指向已卸载组件)。
 */
export const registerFileTransferUI = (
	cb: FileTransferUICallbacks,
): (() => void) => {
	uiCallbacks = cb;
	// 注册即回放当前态:面板可能在 offer 到达之后才挂载/重挂,否则已存在的待确认 offer
	// 与进行中的接收永远不会显示(评审 P1)。
	if (incomingOffer !== null) {
		const o = incomingOffer;
		cb.onIncomingOffer?.({
			id: o.id,
			name: o.name,
			size: o.size,
			mime: o.mime,
		});
	}
	if (activeReceive !== null) {
		cb.onReceiveProgress?.({
			receivedBytes: activeReceive.received,
			totalBytes: activeReceive.size,
		});
	}
	// 回放 host 能力:面板可能在 host hello 之后才挂载,否则发送入口启用状态不会更新(§5)。
	cb.onHostCapabilities?.(hostCapabilities());
	return () => {
		if (uiCallbacks === cb) uiCallbacks = {};
	};
};

const isBusy = (): boolean =>
	activePreparation !== null ||
	(activeSend !== null && !activeSend.done) ||
	activeReceive !== null ||
	incomingOffer !== null;

/** 取消接收 Worker 任务并清空主线程接收态。 */
const discardActiveReceive = (): void => {
	if (activeReceive) fileHashWorker.cancel(activeReceive.workerTaskId);
	activeReceive = null;
};

/** 清空待确认 offer 并通知 UI。 */
const clearIncomingOffer = (): void => {
	incomingOffer = null;
	uiCallbacks.onIncomingOffer?.(null);
};

/** 复位文件传输会话态。peer / data channel 重建/结束时调用(随 `resetNegotiationState`)。 */
export const resetFileTransferState = (): void => {
	peerHelloReceived = false;
	peerCapabilities = new Set();
	peerMaxMessageSize = 0;
	peerMaxFileSize = 0;
	if (helloTimer !== undefined) {
		clearTimeout(helloTimer);
		helloTimer = undefined;
	}
	if (activePreparation) {
		fileHashWorker.cancel(activePreparation.taskId);
		activePreparation.cb.onError?.("File transfer session ended");
		activePreparation = null;
	}
	if (activeSend) activeSend.done = true; // 让 pumpChunks 自然退出
	activeSend = null;
	discardActiveReceive();
	if (incomingOffer !== null) clearIncomingOffer();
	uiCallbacks.onReceiveProgress?.({ receivedBytes: 0, totalBytes: 0 });
	// 通道重建/会话结束:host 能力清零 → 发送入口禁用(下个会话 host hello 再点亮)。
	uiCallbacks.onHostCapabilities?.({ canReceive: false, canSend: false });
};

/**
 * §7.1:`file` 通道 open 后立即发送 `file:hello`(控制端),并启动 5s 超时——
 * 超时未收到对端 hello 则按 unsupported 降级(发送入口据此禁用)。
 */
export const sendFileHello = (channel: RTCDataChannel | null): void => {
	if (!channel || channel.readyState !== "open") return;
	try {
		channel.send(
			encodeFileControl({
				type: "file:hello",
				id: HELLO_ID,
				endpointRole: "controller",
				capabilities: LOCAL_CAPABILITIES,
				maxFileSize: LOCAL_MAX_FILE_SIZE,
				maxMessageSize: LOCAL_MAX_MESSAGE_SIZE,
			}),
		);
		console.log("[file-transfer] file:hello sent (controller)");
	} catch (e) {
		console.warn("[file-transfer] file:hello send failed", e);
	}
	if (helloTimer !== undefined) clearTimeout(helloTimer);
	helloTimer = setTimeout(() => {
		if (!peerHelloReceived) {
			console.warn(
				"[file-transfer] no peer file:hello within timeout — treating as unsupported",
			);
		}
	}, HELLO_TIMEOUT_MS);
};

/**
 * `file` 通道 onmessage。文本帧=控制消息;二进制帧(ArrayBuffer)=§6.2 chunk 帧(作 receiver)。
 * 需通道 `binaryType="arraybuffer"`。
 */
export const handleFileChannelMessage = (event: MessageEvent): void => {
	const data: unknown = event.data;
	if (typeof data !== "string") {
		if (data instanceof ArrayBuffer) onChunk(data);
		return;
	}
	const msg = parseFileControl(data);
	if (!msg) return;
	switch (msg.type) {
		case "file:hello":
			handleHello(msg);
			break;
		case "file:offer": // 作 receiver
			onOffer(msg);
			break;
		case "file:accept": // 作 sender
			onAccept(msg);
			break;
		case "file:reject": // 作 sender
			onReject(msg);
			break;
		case "file:complete": // 作 sender
			onComplete(msg);
			break;
		case "file:cancel":
			onPeerCancel(msg);
			break;
		case "file:error":
			onRemoteError(msg);
			break;
		case "file:progress":
			// 发送时对端落盘进度,暂不消费(sender 已自报)
			break;
		default:
			break;
	}
};

const handleHello = (msg: FileHello): void => {
	const raw = msg as {
		capabilities?: unknown;
		maxMessageSize?: unknown;
		maxFileSize?: unknown;
	};
	const caps = Array.isArray(raw.capabilities)
		? new Set(
				raw.capabilities.filter((c): c is string => typeof c === "string"),
			)
		: new Set<string>();
	peerCapabilities = caps;
	peerMaxMessageSize =
		typeof raw.maxMessageSize === "number" ? raw.maxMessageSize : 0;
	peerMaxFileSize = typeof raw.maxFileSize === "number" ? raw.maxFileSize : 0;
	peerHelloReceived = true;
	if (helloTimer !== undefined) {
		clearTimeout(helloTimer);
		helloTimer = undefined;
	}
	console.log("[file-transfer] file:hello received", {
		capabilities: [...caps],
		maxMessageSize: peerMaxMessageSize,
	});
	// §5:host 能力到达 → 通知 UI 决定发送入口是否可用(host 未声明 receive 即禁用发送)。
	uiCallbacks.onHostCapabilities?.(hostCapabilities());
};

/** 是否已收到对端 hello。 */
export const isPeerHelloReceived = (): boolean => peerHelloReceived;

/** 对端是否就绪可接收文件:已 hello + 声明 `receive` + maxMessageSize 达 v1 下限。 */
export const isPeerReadyToReceive = (): boolean =>
	peerHelloReceived &&
	peerCapabilities.has("receive") &&
	maxMessageSizeSupportsV1(peerMaxMessageSize);

// ── 发送状态机(§8)──────────────────────────────────────────────────

/**
 * 发起一次文件发送。UI 调用 `sendFile` 委托至此。async,但调用方不必 await,
 * 进度/状态/完成/错误全部通过 `cb` 回调驱动 UI。
 */
export const startFileSend = async (
	file: File | null,
	cb: FileSendCallbacks = {},
): Promise<void> => {
	if (!file) {
		cb.onError?.("Please select a file first");
		return;
	}
	if (file.size === 0) {
		cb.onError?.("File is empty, please select a non-empty file");
		return;
	}
	if (isBusy()) {
		cb.onError?.("Another file transfer is in progress");
		return;
	}
	const channel = currentFileChannel();
	if (!channel || channel.readyState !== "open") {
		cb.onError?.("File channel is not open");
		return;
	}
	if (!isPeerReadyToReceive()) {
		cb.onError?.("The other side is not ready to receive files");
		return;
	}
	// §8.4 / §11:发送前执行本端与对端的大小上限,不依赖对端拒绝
	if (file.size > LOCAL_MAX_FILE_SIZE) {
		cb.onError?.(
			`File exceeds the ${Math.floor(LOCAL_MAX_FILE_SIZE / 1024 / 1024)} MiB limit`,
		);
		return;
	}
	if (peerMaxFileSize > 0 && file.size > peerMaxFileSize) {
		cb.onError?.("File exceeds the other side's size limit");
		return;
	}

	cb.onStatus?.(`Preparing ${file.name}…`);
	const preparationTaskId = `send:${newTransferId()}`;
	activePreparation = { taskId: preparationTaskId, cb };
	let sha256: string;
	try {
		sha256 = await fileHashWorker.hashFile(preparationTaskId, file);
	} catch {
		if (activePreparation?.taskId === preparationTaskId) {
			activePreparation = null;
			cb.onError?.("Failed to read file for hashing");
		}
		return;
	}
	if (activePreparation?.taskId !== preparationTaskId) return;
	activePreparation = null;
	if (!channel || channel.readyState !== "open") {
		cb.onError?.("File channel closed before sending");
		return;
	}
	// Hash 是异步 Worker 任务；会话复位会取消 preparation，返回后必须再次确认任务仍有效。
	if (isBusy()) {
		cb.onError?.("Another file transfer started while preparing");
		return;
	}

	const id = newTransferId();
	activeSend = {
		id,
		file,
		size: file.size,
		chunkSize: 0,
		offset: 0,
		seq: 0,
		cb,
		done: false,
	};
	try {
		channel.send(
			encodeFileControl({
				type: "file:offer",
				id,
				name: file.name,
				size: file.size,
				mime: file.type || undefined,
				sha256,
			}),
		);
	} catch {
		failSend("Failed to send file offer");
		return;
	}
	cb.onStatus?.("Waiting for the other side to accept…");
	cb.onProgress?.({ sentBytes: 0, totalBytes: file.size });
};

const onAccept = (msg: FileAccept): void => {
	if (!activeSend || msg.id !== activeSend.id) return;
	const accept: unknown = (msg as { chunkSize?: unknown }).chunkSize;
	if (!isValidAcceptChunkSize(accept)) {
		sendError(activeSend.id, "protocol_error");
		failSend("Invalid chunk size from peer");
		return;
	}
	let chunkSize: number;
	try {
		chunkSize = negotiateChunkSize({
			localPreferred: LOCAL_PREFERRED_CHUNK_SIZE,
			receiverAccept: accept,
			localMaxMessageSize: LOCAL_MAX_MESSAGE_SIZE,
			remoteMaxMessageSize: peerMaxMessageSize,
		});
	} catch {
		sendError(activeSend.id, "protocol_error");
		failSend("Chunk size negotiation failed");
		return;
	}
	activeSend.chunkSize = chunkSize;
	activeSend.cb.onStatus?.(`Sending ${activeSend.file.name}…`);
	void pumpChunks(activeSend.id);
};

/** §8.7-8.9:按协商 chunkSize 流式切片发送,bufferedAmount 背压。 */
const pumpChunks = async (id: string): Promise<void> => {
	const channel = currentFileChannel();
	if (!channel) {
		failSend("File channel unavailable");
		return;
	}
	channel.bufferedAmountLowThreshold = BACKPRESSURE_LOW_BYTES;
	while (true) {
		const s = activeSend;
		if (!s || s.id !== id || s.done) return;
		if (channel.readyState !== "open") {
			failSend("File channel closed during transfer");
			return;
		}
		if (s.offset >= s.size) break;
		if (channel.bufferedAmount > BACKPRESSURE_HIGH_BYTES) {
			try {
				await waitForDataChannelDrain(channel);
			} catch {
				failSend("File channel closed or stalled during transfer");
				return;
			}
			continue;
		}
		const end = Math.min(s.offset + s.chunkSize, s.size);
		let bytes: Uint8Array;
		try {
			bytes = new Uint8Array(await s.file.slice(s.offset, end).arrayBuffer());
		} catch {
			sendError(id, "io_error");
			failSend("File read failed during transfer");
			return;
		}
		if (!activeSend || activeSend.id !== id || activeSend.done) return;
		try {
			const frame = encodeChunkFrame(
				{
					v: 1,
					type: "file:chunk",
					id,
					seq: s.seq,
					offset: s.offset,
					size: bytes.byteLength,
				},
				bytes,
			);
			channel.send(frame);
		} catch {
			sendError(id, "protocol_error");
			failSend("Failed to send chunk");
			return;
		}
		s.offset += bytes.byteLength;
		s.seq += 1;
		s.cb.onProgress?.({ sentBytes: s.offset, totalBytes: s.size });
	}
	activeSend?.cb.onStatus?.("Waiting for completion…");
};

const onComplete = (msg: FileComplete): void => {
	if (!activeSend || msg.id !== activeSend.id) return;
	activeSend.cb.onStatus?.(`File ${activeSend.file.name} sent successfully`);
	activeSend.cb.onComplete?.();
	activeSend.done = true;
	activeSend = null;
};

const onReject = (msg: FileReject): void => {
	if (!activeSend || msg.id !== activeSend.id) return;
	const reason = typeof msg.reason === "string" ? msg.reason : "unknown";
	failSend(`Transfer rejected by peer: ${reason}`);
};

const failSend = (message: string): void => {
	if (activeSend) {
		activeSend.cb.onError?.(message);
		// 复位发送进度,否则取消/拒绝/出错后 Cancel 按钮(totalBytes>0 && sent<total)残留(评审 P2)
		activeSend.cb.onProgress?.({ sentBytes: 0, totalBytes: 0 });
		activeSend.done = true;
	}
	activeSend = null;
};

// ── 接收状态机(§9,host→controller)──────────────────────────────────

const onOffer = (msg: FileOffer): void => {
	const raw = msg as {
		id?: unknown;
		name?: unknown;
		size?: unknown;
		sha256?: unknown;
		mime?: unknown;
	};
	const id = typeof raw.id === "string" ? raw.id : "";
	if (!id) return;
	const channel = currentFileChannel();
	if (!channel || channel.readyState !== "open") return;

	// §4.1 glare:本端有"已发出未被接受"的 pending send(chunkSize===0,未进入 sending)时
	// 收到 incoming offer,按 transfer id ASCII 字典序 tie-break(resolveGlare)。
	const pendingSend =
		activeSend !== null && !activeSend.done && activeSend.chunkSize === 0
			? activeSend
			: null;
	if (pendingSend) {
		const outcome = resolveGlare(pendingSend.id, id);
		if (outcome === "collision") {
			// id 相同:违反 §6.1 唯一性的兜底。双方对该 id 发 file:error 并丢弃两者。
			sendError(id, "protocol_error");
			failSend("Transfer collision (duplicate id), aborted");
			console.warn("[file-transfer] glare collision on id", id);
			return;
		}
		if (outcome === "local-wins") {
			// 本端 offer 胜出:对 incoming 回 reject superseded,保留本端 pending send。
			channel.send(
				encodeFileControl({ type: "file:reject", id, reason: "superseded" }),
			);
			return;
		}
		// incoming-wins:撤回本端 pending send(发 cancel superseded),再把 incoming 暂存待确认。
		channel.send(
			encodeFileControl({
				type: "file:cancel",
				id: pendingSend.id,
				reason: "superseded",
			}),
		);
		failSend("Your transfer was superseded by an incoming file");
		// 落到下面的暂存逻辑。
	} else if (isBusy()) {
		// 已进入 accepted/sending/receiving,或已有待确认 offer:新 offer 直接 busy(§4.1.3 / §4)。
		channel.send(
			encodeFileControl({ type: "file:reject", id, reason: "busy" }),
		);
		return;
	}

	const size = typeof raw.size === "number" ? raw.size : -1;
	if (size < 1 || size > LOCAL_MAX_FILE_SIZE) {
		channel.send(
			encodeFileControl({ type: "file:reject", id, reason: "too_large" }),
		);
		return;
	}
	// §7.2:sha256 必填且为 64 位 hex。缺失/非法在 offer 阶段即按 protocol_error 终止,
	// 不进入用户确认/接收(否则要传完整文件后才在 checksum 阶段失败)(评审 P2)。
	const sha256 = typeof raw.sha256 === "string" ? raw.sha256 : "";
	if (!/^[0-9a-fA-F]{64}$/.test(sha256)) {
		channel.send(
			encodeFileControl({ type: "file:error", id, code: "protocol_error" }),
		);
		return;
	}
	const name = sanitizeFileName(typeof raw.name === "string" ? raw.name : "");
	const mime =
		typeof raw.mime === "string" ? raw.mime : "application/octet-stream";
	// §5 / §7.2:暂存待确认,通知 UI 显示来源/文件名/大小,不自动 accept/reject。
	incomingOffer = { id, name, size, mime, sha256 };
	uiCallbacks.onIncomingOffer?.({ id, name, size, mime });
	uiCallbacks.onReceiveStatus?.(`Incoming file: ${name}`);
	console.log("[file-transfer] incoming offer awaiting confirmation", {
		id,
		name,
		size,
	});
};

/**
 * 用户接受待确认 offer(§7.3):建 activeReceive 并发 file:accept。
 */
export const acceptIncomingOffer = (): void => {
	const offer = incomingOffer;
	if (!offer) return;
	const channel = currentFileChannel();
	if (!channel || channel.readyState !== "open") {
		clearIncomingOffer();
		uiCallbacks.onReceiveStatus?.("File channel is not open");
		return;
	}
	const workerTaskId = `receive:${offer.id}`;
	try {
		fileHashWorker.startReceive(workerTaskId, offer.mime);
	} catch {
		clearIncomingOffer();
		uiCallbacks.onReceiveStatus?.("Failed to prepare file receiver");
		return;
	}
	activeReceive = {
		id: offer.id,
		name: offer.name,
		size: offer.size,
		sha256: offer.sha256,
		mime: offer.mime,
		workerTaskId,
		received: 0,
		nextSeq: 0,
		finalizing: false,
	};
	incomingOffer = null;
	uiCallbacks.onIncomingOffer?.(null);
	try {
		channel.send(
			encodeFileControl({
				type: "file:accept",
				id: offer.id,
				chunkSize: LOCAL_PREFERRED_CHUNK_SIZE,
			}),
		);
	} catch {
		discardActiveReceive();
		uiCallbacks.onReceiveStatus?.("Failed to accept file");
		return;
	}
	uiCallbacks.onReceiveStatus?.(`Receiving ${offer.name}…`);
	uiCallbacks.onReceiveProgress?.({ receivedBytes: 0, totalBytes: offer.size });
	console.log("[file-transfer] receiving", {
		id: offer.id,
		name: offer.name,
		size: offer.size,
	});
};

/**
 * 用户拒绝待确认 offer(§7.4):发 file:reject reason="user_rejected" 并清空。
 */
export const rejectIncomingOffer = (): void => {
	const offer = incomingOffer;
	if (!offer) return;
	const channel = currentFileChannel();
	if (channel && channel.readyState === "open") {
		try {
			channel.send(
				encodeFileControl({
					type: "file:reject",
					id: offer.id,
					reason: "user_rejected",
				}),
			);
		} catch {
			// best-effort
		}
	}
	clearIncomingOffer();
	uiCallbacks.onReceiveStatus?.("File rejected");
};

/**
 * 取消当前文件传输(§7.7,user_cancelled):
 * - 活动发送:发 file:cancel + 终止发送状态机。
 * - 活动接收:发 file:cancel + 清理接收态。
 * - 仅待确认 offer:等同 reject。
 */
export const cancelFileTransfer = (): void => {
	const channel = currentFileChannel();
	if (activeSend && !activeSend.done) {
		const id = activeSend.id;
		if (channel && channel.readyState === "open") {
			try {
				channel.send(
					encodeFileControl({
						type: "file:cancel",
						id,
						reason: "user_cancelled",
					}),
				);
			} catch {
				// best-effort
			}
		}
		failSend("Transfer cancelled");
		return;
	}
	if (activeReceive) {
		const id = activeReceive.id;
		if (channel && channel.readyState === "open") {
			try {
				channel.send(
					encodeFileControl({
						type: "file:cancel",
						id,
						reason: "user_cancelled",
					}),
				);
			} catch {
				// best-effort
			}
		}
		discardActiveReceive();
		uiCallbacks.onReceiveProgress?.({ receivedBytes: 0, totalBytes: 0 });
		uiCallbacks.onReceiveStatus?.("Receive cancelled");
		return;
	}
	if (incomingOffer) rejectIncomingOffer();
};

const onChunk = (buf: ArrayBuffer): void => {
	const r = activeReceive;
	if (!r) return;
	const channel = currentFileChannel();
	const decoded = decodeChunkFrame(buf);
	if (!decoded) {
		channel?.send(
			encodeFileControl({
				type: "file:error",
				id: r.id,
				code: "protocol_error",
			}),
		);
		discardActiveReceive();
		return;
	}
	const { header, payload } = decoded;
	if (
		header.id !== r.id ||
		header.seq !== r.nextSeq ||
		header.offset !== r.received ||
		r.received + payload.byteLength > r.size
	) {
		channel?.send(
			encodeFileControl({
				type: "file:error",
				id: r.id,
				code: "protocol_error",
			}),
		);
		discardActiveReceive();
		return;
	}
	const payloadSize = payload.byteLength;
	try {
		fileHashWorker.appendReceiveChunk(
			r.workerTaskId,
			payload.buffer as ArrayBuffer,
		);
	} catch {
		sendError(r.id, "io_error");
		discardActiveReceive();
		uiCallbacks.onReceiveStatus?.("Failed to buffer received file");
		return;
	}
	r.received += payloadSize;
	r.nextSeq += 1;
	uiCallbacks.onReceiveProgress?.({
		receivedBytes: r.received,
		totalBytes: r.size,
	});
	if (r.received >= r.size && !r.finalizing) {
		r.finalizing = true;
		void finalizeReceive(r);
	}
};

const finalizeReceive = async (r: ActiveReceive): Promise<void> => {
	const channel = currentFileChannel();
	let result: { sha256: string; blob: Blob };
	try {
		result = await fileHashWorker.finalizeReceive(r.workerTaskId);
	} catch {
		if (activeReceive?.id !== r.id) return;
		sendError(r.id, "io_error");
		discardActiveReceive();
		uiCallbacks.onReceiveStatus?.("File verification failed");
		uiCallbacks.onReceiveProgress?.({ receivedBytes: 0, totalBytes: 0 });
		return;
	}
	const digest = result.sha256;
	if (activeReceive?.id !== r.id) return; // 期间被复位/取消
	if (digest.toLowerCase() !== r.sha256.toLowerCase()) {
		channel?.send(
			encodeFileControl({
				type: "file:error",
				id: r.id,
				code: "checksum_mismatch",
			}),
		);
		discardActiveReceive();
		uiCallbacks.onReceiveStatus?.(
			"File verification failed (checksum mismatch)",
		);
		uiCallbacks.onReceiveProgress?.({ receivedBytes: 0, totalBytes: 0 });
		return;
	}
	triggerDownload(result.blob, r.name);
	channel?.send(
		encodeFileControl({
			type: "file:complete",
			id: r.id,
			receivedBytes: r.received,
			sha256: digest,
		}),
	);
	discardActiveReceive();
	uiCallbacks.onReceiveStatus?.(`File ${r.name} downloaded`);
	// 与其余结束路径(checksum 失败 / 取消 / 对端 cancel/error)一致复位进度,
	// 否则进度条停在 100% 且 Cancel 按钮残留到下次会话复位(评审 medium)。
	uiCallbacks.onReceiveProgress?.({ receivedBytes: 0, totalBytes: 0 });
	console.log("[file-transfer] received & downloaded", r.name);
};

/** §7.2:Console 接收 → 浏览器下载(Blob URL + 隐藏 anchor)。 */
const triggerDownload = (blob: Blob, name: string): void => {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = name;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

// ── 双向取消 / 错误 ──────────────────────────────────────────────────

const onPeerCancel = (msg: { id?: unknown }): void => {
	const id = msg.id;
	if (incomingOffer && id === incomingOffer.id) {
		// 对端在我们确认前撤回了 offer(例如 glare superseded)。
		clearIncomingOffer();
		uiCallbacks.onReceiveStatus?.("Incoming file withdrawn by sender");
		console.log("[file-transfer] incoming offer cancelled by peer");
		return;
	}
	if (activeReceive && id === activeReceive.id) {
		discardActiveReceive();
		uiCallbacks.onReceiveProgress?.({ receivedBytes: 0, totalBytes: 0 });
		uiCallbacks.onReceiveStatus?.("Receive cancelled by sender");
		console.log("[file-transfer] receive cancelled by peer");
	} else if (activeSend && id === activeSend.id) {
		failSend("Transfer cancelled by peer");
	}
};

const onRemoteError = (msg: FileError): void => {
	const id = msg.id;
	if (incomingOffer && id === incomingOffer.id) {
		clearIncomingOffer();
		uiCallbacks.onReceiveStatus?.("Incoming file failed");
		return;
	}
	if (activeReceive && id === activeReceive.id) {
		discardActiveReceive();
		uiCallbacks.onReceiveProgress?.({ receivedBytes: 0, totalBytes: 0 });
		uiCallbacks.onReceiveStatus?.("Receive failed (peer error)");
		return;
	}
	if (activeSend && id === activeSend.id) {
		const code = typeof msg.code === "string" ? msg.code : "unknown";
		failSend(`Transfer error from peer: ${code}`);
	}
};

const sendError = (id: string, code: FileErrorCode): void => {
	const channel = currentFileChannel();
	if (!channel || channel.readyState !== "open") return;
	try {
		channel.send(encodeFileControl({ type: "file:error", id, code }));
	} catch {
		// best-effort
	}
};
